import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Modal, Space, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import AddFractionnement from "./AddFractionnement";
import ActionDropdown from "../../production/components/ActionDropdown";
import axiosInstance from "../../../variables/api/axiosInstance";
import { useNavigate } from "react-router-dom";
import TauxFractionnement from "./TauxFractionnement";

const { confirm } = Modal;

const { Title } = Typography;

const Fractionnements = () => {
  const [fractionnements, setFractionnements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedFractionnement, setSelectedFractionnement] = useState(null);
  const [line, setLine] = useState(null);
  const [isModalOpenLine, setIsModalOpenLine] = useState(false);
  const [tauxFractionnements, setTauxFractionnements] = useState([]);

  const navigate = useNavigate();

  const handleShow = (edit, fractionnement = null) => {
    setEditMode(edit);
    setSelectedFractionnement(fractionnement);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedFractionnement(null); // Clear selected fractionnement on close
  };

  useEffect(() => {
    fetchFractionnements();
  }, [isModalOpenLine]);

  const fetchFractionnements = () => {
    axiosInstance
      .get(`/api/fractionnement`)
      .then((res) => {
        // Sort from newest to oldest by ID
        const sorted = [...res.data].sort((a, b) => b.fractionnementId - a.fractionnementId);
        setFractionnements(sorted);
      })
      .catch((err) => {
        console.error("Error fetching fractionnements:", err);
        toast.error("Erreur lors du chargement des fractionnements");
      });
  };

  useEffect(() => {
    axiosInstance.get("/api/taux-fractionnement").then((res) => {
      setTauxFractionnements(res.data);
    }).catch((err) => {
      console.error("Error fetching taux fractionnements:", err);
    });
  }, [isModalOpenLine]);

  const columns = [
    { title: "Nom", dataIndex: "libelleFractionnement", key: "libelleFractionnement" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Nombre", dataIndex: "nombre", key: "nombre" },
    {
      title: "Pér. min",
      dataIndex: "moisMin",
      key: "moisMin",
      render: (_, record) => `${record.moisMin} ${record.periode === "JOUR" ? "JOURS" : record.periode || "MOIS"}`,
    },
    {
      title: "Pér. max",
      dataIndex: "moisMax",
      key: "moisMax",
      render: (_, record) => `${record.moisMax} ${record.periode === "JOUR" ? "JOURS" : record.periode || "MOIS"}`,
    },
    {
      title: "Catégorie",
      dataIndex: "categorie",
      key: "categorie",
      render: (cat) => (cat ? cat.nom : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <i className="bx bx-edit-alt" />,
              onClick: () => handleShow(true, record),
            },
            {
              key: "details",
              label: "Details",
              icon: <i className="bx bx-detail" />,
              onClick: () => navigate(`/taux-fractionnement/${record.fractionnementId}`),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <i className="bx bx-trash" />,
              danger: true,
              onClick: () => showDeleteConfirm(record.fractionnementId),
            },
          ]}
        />
      ),
    },
  ];

  const tauxColumns = [
    {
      title: 'RANG',
      dataIndex: 'rang',
      key: 'rang',
    },
    {
      title: 'LIBELLE',
      dataIndex: 'libelleTauxFractionnement',
      key: 'libelleTauxFractionnement',
    },
    {
      title: 'TAUX',
      dataIndex: 'taux',
      key: 'taux',
      render: (_, record) => `${record.taux}%`,
    },
  ];

  const showDeleteConfirm = (fractionnementId) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce fractionnement?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        deleteFractionnement(fractionnementId);
      },
    });
  };

  const deleteFractionnement = (fractionnementId) => {
    axiosInstance
      .delete(`/api/fractionnement/${fractionnementId}`)
      .then(() => {
        setFractionnements((prev) => prev.filter(f => f.fractionnementId !== fractionnementId));
        toast.success("Fractionnement supprimé avec succès");
      })
      .catch((err) => {
        console.error("Error deleting fractionnement:", err);
        toast.error("Erreur lors de la suppression");
      });
  };

  const handleSaved = () => {
    // Re-fetch all fractionnements to ensure the list is up-to-date
    // after an add or update operation.
    fetchFractionnements();
    handleClose();
  };

  const expandedTauxFractionnementRowRender = (record) => (
    <Card
      type="inner"
      style={{
        margin: '16px 32px',
        backgroundColor: '#F9FAFC',
        borderColor: '#E0E6F1',
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(39, 35, 102, 0.05)',
      }}
      bodyStyle={{ padding: 24 }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ textAlign: 'center' }}>
          <Title level={4} style={{ marginBottom: 4, color: '#272366' }}>Taux de fractionnement</Title>
          {/* <Text type="secondary">Détail des lignes d’écriture comptable</Text> */}
        </div>
        <Table
          bordered
          columns={tauxColumns}
          rowClassName={(record) => (record.key === line?.tauxFractionnementId ? "night-blue-row" : "")}
          dataSource={tauxFractionnements?.filter((l) => l.fractionnement?.fractionnementId === record?.fractionnementId)
            ?.slice() // copie pour éviter de muter l'original
            .map((l) => ({
              key: l.tauxFractionnementId,
              rang: l.rang,
              libelleTauxFractionnement: l.libelleTauxFractionnement,
              taux: l.taux,
              data: l
            }))
          }
          pagination={false}
          size="small"
          rowKey="id"
          style={{ border: '1px solid #E0E6F1', borderRadius: 4 }}
          onRow={(lineRecord, index) => {
            return {
              onClick: (event) => {
                setEditMode(true);
                console.log(lineRecord);
                console.log(record);
                setSelectedFractionnement(record);
                setLine(lineRecord.data);
                setIsModalOpenLine(true);
              },
              onContextMenu: (event) => {
                event.preventDefault();
                console.log(lineRecord);
                setLine(lineRecord.data);
                // confirmLineDeletion(lineRecord.data, record);
              }
            }
          }}
        />
        <Divider />
        <div style={{ textAlign: 'center' }}>
          <Title level={5} style={{ color: '#272366' }}>Ajouter un détail de fractionnement</Title>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{
              backgroundColor: '#00BFFF',
              borderColor: '#00BFFF',
              color: '#fff',
            }}
            onClick={() => {
              setIsModalOpenLine(true);
              setSelectedFractionnement(record);
            }}
          >
            Nouveau taux
          </Button>
        </div>
      </Space>
    </Card>
  );


  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"TARIFICATION"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow(false)}
              >
                Ajouter un nouveau fractionnement
              </Button>
              <div className="card">
                <Table
                  columns={columns}
                  dataSource={fractionnements}
                  rowKey="fractionnementId"
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  expandable={{
                    expandedRowRender: expandedTauxFractionnementRowRender,
                    defaultExpandedRowKeys: ['0'],
                    expandRowByClick: true,
                  }}
                  style={{ backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden' }}
                  bordered
                />
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <AddFractionnement
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedFractionnement={selectedFractionnement}
        onSaved={handleSaved}
      />
      <TauxFractionnement
        open={isModalOpenLine}
        setOpen={setIsModalOpenLine}
        tauxFractionnement={line}
        fractionnement={selectedFractionnement}
        edit={editMode}
      />
    </div>
  );
};

export default Fractionnements;